import {
  Component,
  forwardRef,
  signal,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractInputComponent } from '../abstract-input.component';
import { AbstractNgModelComponent } from '../abstract-ng-model.component';
import { NzStatus } from 'ng-zorro-antd/core/types';
import { DateManagerService } from '@core/services/date-manager/date-manager.service';
import { differenceInCalendarDays } from 'date-fns';
import { Store } from '@ngrx/store';
import { selectCurrentOrganizationTimeZoneIanaId } from 'src/app/store/current-user/current-user.selector';
import { GlobalFunctions } from '@shared/utility/global-functions';

const daysFromNow = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date;
};
const daysFromDate = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};
@Component({
  selector: 'xpw-form-manual-dates',
  template: `
    <div>
      <!-- Placeholder for future implementation -->
      <section>
        <div class="flex flex-row gap-2">
          <xpw-input-date
            [value]="startDate().value"
            [readonly]="startDate().readonly"
            (dateChange)="startDateChanged($event)"
            [status]="startDate().status"
            label="Start date"
          />
          <xpw-input-time
            [value]="time"
            (onChange)="timeChanged($event)"
            [status]="startDate().status"
            label="Start time"
            [specificDisabledHours]="disableHours"
          />
        </div>

        @for (date of listOfDates(); track $index) {
          <div>
            <xpw-input-date
              [label]="'Report ' + ($index + 1)"
              (dateChange)="listDateChanged($event, $index)"
              [minDate]="date.minDate"
              [maxDate]="date.maxDate"
              [value]="date.value"
              [readonly]="date.readonly"
              [status]="date.status"
            >
              <xpw-button type="link" (click)="removeDate($index)">
                <xpw-icon icon="delete" />
              </xpw-button>
            </xpw-input-date>
          </div>
        }
        <xpw-button type="dashed" (click)="addDate()">
          <xpw-icon icon="add-plus" /> Add Next Report</xpw-button
        >
      </section>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => XpwFormManualDatesComponent),
      multi: true,
    },
  ],
})
export class XpwFormManualDatesComponent
  extends AbstractNgModelComponent<any>
  implements OnInit
{
  disableHours = [];
  // Add flag to prevent initial value from marking form as dirty
  private isInitializing = true;
  private daysDiff = 1;
  private dateService = inject(DateManagerService);
  // Add flag to allow external control over when to mark form as dirty
  private store = inject(Store);
  currentOrganizationTimeZoneIanaId = this.store.selectSignal(
    selectCurrentOrganizationTimeZoneIanaId,
  );
  @Input() markAsDirtyOnChange = true;

  listOfDates = signal<
    {
      value: Date;
      minDate: Date;
      maxDate?: Date;
      readonly?: boolean;
      status?: NzStatus;
    }[]
  >([
    {
      value: this.dateService.getOnlyDate(daysFromNow(1)),
      minDate: this.dateService.getOnlyDate(daysFromNow(-1)),
      readonly: false,
      status: '',
    },
  ]);

  startDate = signal<{
    value: Date;
    minDate: Date;
    readonly?: boolean;
    status?: NzStatus;
    error?: string;
  }>({
    value: this.dateService.getOnlyDate(daysFromNow(-1)),
    minDate: this.dateService.getOnlyDate(daysFromNow(-1)),
    readonly: false,
    status: '',
    error: 'required',
  });

  time: Date | null = new Date(new Date().setHours(0, 0, 0, 0));

  hasDatePassed = (date: Date): boolean => {
    if (!date || isNaN(date.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate <= today;
  };

  // Utility method to ensure dates are always Date objects
  ensureDate(date: Date | string | null | undefined): Date | null {
    if (!date) return null;

    const parsedDate = date instanceof Date ? date : new Date(date);
    const dateOnly = this.dateService.getOnlyDate(parsedDate);
    // Check if date is valid, if not return null
    return isNaN(dateOnly.getTime()) ? null : dateOnly;
  }

  /**
   * Calculate the minimum date for a specific item in the list
   */
  calculateMinDate(item: any, index: number, list: any[]): Date {
    return index === 0
      ? this.calculateMinDateFirstReport()
      : list[index - 1].value;
  }

  /**
   * Calculate the maximum date for a specific item in the list
   */
  calculateMaxDate(item: any, index: number, list: any[]): Date | null {
    return index === list.length - 1
      ? null
      : daysFromDate(list[index + 1].value, -1);
  }

  /**
   * Calculate the readonly status for a specific item in the list
   */
  calculateReadonlyState(item: any, index: number, list: any[]): boolean {
    return false; // Currently always false, but keeping the logic separate
    // return this.hasDatePassed(item.value); // Original commented logic
  }

  /**
   * Calculate the validation status for a specific item in the list
   */
  calculateValidationStatus(
    item: any,
    index: number,
    list: any[],
    readonly: boolean,
    minDate: Date,
  ): NzStatus {
    const isError =
      readonly ||
      (item.value && minDate && item.value <= minDate) ||
      !item.value;
    return isError ? 'error' : '';
  }

  /**
   * Main function that orchestrates the update of all date item properties
   */
  updateDateItemProperties() {
    this.listOfDates.update((list) =>
      list.map((item, index) => {
        const minDate = this.calculateMinDate(item, index, list);
        const maxDate = this.calculateMaxDate(item, index, list);
        const readonly = this.calculateReadonlyState(item, index, list);
        const status = this.calculateValidationStatus(
          item,
          index,
          list,
          readonly,
          minDate,
        );

        return { ...item, minDate, maxDate, readonly, status };
      }),
    );
    this.updateValue();
  }

  calculateMinDateFirstReport() {
    // Make sure we have a valid start date
    const startDate = this.startDate().value;
    if (!startDate || isNaN(startDate.getTime())) {
      return this.dateService.getOnlyDate(daysFromNow(-1)); // Use today as fallback
    }

    const minDate = this.hasDatePassed(startDate)
      ? this.getMinDateForPassedStartDate(this.listOfDates()[0].value)
      : startDate;

    return minDate;
  }

  private getMinDateForPassedStartDate(firstDate: Date): Date {
    // Check if the start date is today
    const today = new Date();
    const isToday =
      firstDate.getFullYear() === today.getFullYear() &&
      firstDate.getMonth() === today.getMonth() &&
      firstDate.getDate() === today.getDate();

    if (isToday) {
      // Call GlobalFunctions.getPassedHoursByTimezone when user selects today
      const timeZone = this.currentOrganizationTimeZoneIanaId();
      if (timeZone) {
        try {
          const now = new Date();
          const utcDateTimeString = now.toISOString();
          this.disableHours = GlobalFunctions.getPassedHoursByTimezone(
            utcDateTimeString,
            timeZone,
          );
          console.log(
            'User selected today as start date. Passed hours:',
            this.disableHours,
          );

          // You can use passedHours here for additional logic if needed
          // For now, we still return yesterday as the minimum date
          return this.dateService.getOnlyDate(daysFromNow(-1));
        } catch (error) {
          console.error('Error getting passed hours by timezone:', error);
        }
      } else {
        console.warn('No timezone available for passed hours calculation');
      }
    }
    this.disableHours = [];
    // Default behavior: return yesterday
    return this.dateService.getOnlyDate(daysFromNow(-1));
  }
  // Updated listDateChanged to ensure date is a Date object
  listDateChanged(date: Date | string, index: number) {
    const dateObj = this.ensureDate(date);
    this.listOfDates.update((list) =>
      list.map((item, i) => {
        if (i === index) {
          return { ...item, value: dateObj };
        }
        return item;
      }),
    );
    this.updateDateItemProperties();
  }

  addDate() {
    const prevIndex = this.listOfDates().length - 1;
    const minDate: Date =
      prevIndex < 0
        ? this.startDate().value
        : this.listOfDates()[prevIndex].value;

    // Calculate the new date value based on the previous date + daysDiff
    const calculatedDate = this.dateService.getOnlyDate(
      new Date(minDate.getTime() + this.daysDiff * 24 * 60 * 60 * 1000),
    );

    // Ensure the minimum date is tomorrow
    const tomorrow = this.dateService.getOnlyDate(daysFromNow(0));
    const finalMinDate = new Date(Math.max(minDate.getTime(), tomorrow.getTime()));
    
    // Ensure the calculated date is not before the minimum date
    const finalDate = new Date(Math.max(calculatedDate.getTime(), finalMinDate.getTime()));

    this.listOfDates.update((prev) => [
      ...prev,
      {
        value: finalDate,
        minDate: finalMinDate,
        readonly: this.hasDatePassed(finalMinDate),
      },
    ]);

    this.updateDateItemProperties();
  }

  startDateChanged(date: any) {
    this.startDate.set({
      ...this.startDate(),
      value: this.ensureDate(date),
      status: date == null ? 'error' : '',
      error: date == null ? 'required' : '',
    });
    this.updateDateItemProperties(); // Make sure to update min dates when start date changes
  }

  timeChanged(date: any) {
    // Validate that we have a proper date
    try {
      this.time = date;

      // If the date is invalid, set as null
      if (this.time && isNaN(this.time.getTime())) {
        this.time = null;
      }
    } catch (e) {
      // Any error would mean we have an invalid date
      this.time = null;
    }

    // Update status based on time validity
    // this.startDate.set({
    //   ...this.startDate(),
    //   status: this.time == null ? 'error' : '',
    // });

    this.updateDateItemProperties();
  }

  removeDate(index: number) {
    this.listOfDates.update((prev) => prev.filter((_, i) => i !== index));
    this.updateDateItemProperties();
  }

  ngOnInit(): void {
    // After a brief delay, initialization is complete
    setTimeout(() => {
      this.isInitializing = false;
    }, 0);
    console.log(
      'this.currentOrganizationTimeZoneIanaId()',
      this.currentOrganizationTimeZoneIanaId(),
    );
  }

  // Updated writeValue to ensure all dates are Date objects
  override writeValue(value: any): void {
     this.isInitializing = true; // Set flag to prevent onChange from marking form as dirty

    if (!value) {
      setTimeout(() => {
        this.isInitializing = false;
        // Add this line to ensure the form value is updated after initialization
        this.updateValue();
      }, 0);
      return;
    }

    if (value.startDate) {
      const startDateValue = this.ensureDate(value.startDate);
      // Only update if we have a valid date
      if (startDateValue) {
        this.startDate.set({
          value: startDateValue,
          minDate: startDateValue,
          readonly: this.hasDatePassed(startDateValue),
          status: '', // Reset status on valid date
        });
      } else {
        // Handle invalid date by using current date as fallback
        const fallbackDate = this.dateService.getOnlyDate(daysFromNow(-1));
        this.startDate.set({
          value: fallbackDate,
          minDate: fallbackDate,
          readonly: false,
          status: '', // Reset status
        });
      }
    }

    if (value.hour !== undefined && value.minute !== undefined) {
      try {
        const date = new Date();
        date.setHours(value.hour || 0, value.minute || 0);
        this.time = isNaN(date.getTime()) ? new Date() : date;
      } catch (e) {
        // If there's an error, use current time as fallback
        this.time = new Date();
      }
    }

    if (value.dates?.length) {
      try {
        const validDates = value.dates
          .map((date: Date | string) => this.ensureDate(date))
          .filter((date) => date !== null);

        // If we have no valid dates, use a default one
        if (validDates.length === 0) {
          this.listOfDates.set([
            {
              value: this.dateService.getOnlyDate(daysFromNow(2)),
              minDate: this.dateService.getOnlyDate(daysFromNow(-1)),
              readonly: false,
              status: '',
            },
          ]);
        } else {
          this.listOfDates.set(
            validDates.map((date: Date) => ({
              value: date,
              minDate: new Date(), // Temporary value, will be updated
              readonly: false, // Temporary value, will be updated
              status: '',
            })),
          );
        }

        this.updateDateItemProperties(); // Apply minDate and readonly after setting initial values
      } catch (e) {
        // If there's an error, use default values
        this.listOfDates.set([
          {
            value: this.dateService.getOnlyDate(daysFromNow(2)),
            minDate: this.dateService.getOnlyDate(daysFromNow(-1)),
            readonly: false,
            status: '',
          },
        ]);
      }
    }

    // Reset flag after short delay to ensure writeValue completes
   
  }

  updateValue() {
    const value = {
      startDate: this.startDate().value,
      dates: this.listOfDates().map((date) => date.value),
      hour: this.time?.getHours() ?? null,
      minute: this.time?.getMinutes() ?? null,
    };

    // Calculate days difference
    const dates = value.dates;
    if (dates.length > 1) {
      this.daysDiff = Math.abs(
        differenceInCalendarDays(
          dates[dates.length - 1],
          dates[dates.length - 2],
        ),
      );
    } else if (dates.length === 1) {
      this.daysDiff = Math.abs(
        differenceInCalendarDays(dates[0], value.startDate),
      );
    } else {
      this.daysDiff = 1; // Default value if no dates are set
    }

    // Only call onChange and onTouched if not initializing
    if (!this.isInitializing) {
      // Only mark as dirty when markAsDirtyOnChange is true
      if (this.markAsDirtyOnChange) {
        this.onChange(value);
        this.onTouched();
      } else {
        // Still update the value in the form model but don't mark as dirty
        if (this.onChange) {
          this.onChange(value);
        }
      }
    }
  }
}
